import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ClientMemoryApi} from '../../../../api/clientMemoryApi';
import {YesNoModal} from '../../../elements/YesNoModal';
import {DOC_STATUS} from '../../memory/docConstants';
import {BoosterNarrator} from './BoosterNarrator';
import {IngestActivityList, isInFlight} from './IngestActivityList';
import {SourceLine} from './SourceLine';
import {DocumentKindModal} from '../../memory/DocumentKindModal';

// Cadencia del refresco mientras hay extracción en curso. Es el MISMO número que usa
// `MemoryDocumentsView._schedulePollIfNeeded`: si los dos ritmos difirieran, la tabla de documentos
// y esta lista se contarían distinto la misma extracción durante varios segundos.
export const FEED_POLL_MS = 8000;

// Cuántas entradas muestra la lista de ingesta. No es la biblioteca del cliente (esa es la tabla de
// Fuentes, a un clic de la línea de abajo): es lo que está pasando ahora y lo que acaba de pasar.
const MAX_FEED_ITEMS = 6;

// Cuántas vueltas malas SEGUIDAS aguanta el poll antes de rendirse.
//
// `APIClient` nunca rechaza: un blip de red vuelve como `{status:'NETWORK_ERROR'}`, o sea una
// respuesta sin `documents`. Antes ese caso salía por un `return` que no reagendaba el timer, así
// que UNA respuesta mala mataba el poll para siempre — y como los polls corren con `silent=true`,
// tampoco había toast. Consecuencia en pantalla: las filas quedaban clavadas en «En cola…» /
// «Clasificando y extrayendo…», el narrador nunca decía que había terminado y `onDocumentReady`
// no se disparaba, así que el mazo de Revisa no se enteraba de los hechos nuevos hasta un F5.
//
// Tres reintentos son ~24s de tolerancia: alcanza para un blip y no convierte un backend caído en
// un poll eterno.
export const MAX_POLL_RETRIES = 3;

/**
 * El acto Alimenta: narrador, lo que está entrando y de qué está hecha la memoria. El instructivo
 * (la guía de qué material sirve) ya no vive acá: subió arriba del orbe, en `FeedGuideBlock`.
 *
 * ⚠️ **Acá ya NO hay puerta de carga.** Hasta el 08/09 vivía debajo del orbe un compositor plegado
 * («Escribir, pegar o adjuntar»), con su caja de texto, su zona de drop, su título y su selector de
 * tipo. Se sacó entero (card ScPxmmB3): era un segundo editor treinta píxeles debajo de la Barra
 * Total, que hace lo mismo —«aparece de vuelta un editor acá cuando ya lo tenés arriba»—. Lo único
 * suyo que la barra no tenía, el TIPO declarado, se mudó a `BoosterOmnibar`.
 *
 * **Es el dueño del dato.** Un solo `listDocuments` + `listMemories` para todo el acto, un solo
 * poll, y los hijos reciben todo por props. La alternativa —que la lista de ingesta y la línea de
 * fuentes pidieran cada una lo suyo— son tres consultas al mismo endpoint en la misma pantalla, con
 * tres respuestas que pueden discrepar.
 *
 * **El poll corre SÓLO mientras haya documentos en vuelo** (PENDING/EXTRACTING), igual que
 * `MemoryDocumentsView`: la extracción es asíncrona y dura decenas de segundos, pero cuando no hay
 * nada extrayéndose no hay nada que mirar. Cuando la última fila llega a READY o FAILED, el timer
 * no se vuelve a agendar.
 *
 * **Pero una respuesta mala no lo apaga.** `APIClient` nunca rechaza —un blip vuelve como
 * `{status:'NETWORK_ERROR'}`, sin `documents`— y esa vuelta se reagenda igual, hasta
 * `MAX_POLL_RETRIES` veces seguidas. Si se agotan, la lista lo DICE y ofrece volver a mirar, en vez
 * de dejar filas anunciando «Clasificando…» sobre una extracción que ya nadie está siguiendo.
 *
 * ⚠️ **DOBLE POLL — residuo conocido.** En la one-page, el mismo acto monta `MemorySourcesView`, que
 * contiene `MemoryDocumentsView`, que pollea `agent/client-document/` con esta misma cadencia. No se
 * puede consumir de ahí sin tocar `memory/` (esa vista no publica sus documentos hacia afuera), así
 * que la ventana de solapamiento existe: mientras algo se extrae Y el panel de Fuentes está montado,
 * son dos requests cada 8s en vez de uno. Se acota de dos formas: `pollDocuments={false}` apaga este
 * poll cuando el contenedor sabe que la tabla ya está en pantalla, y el solapamiento termina solo
 * cuando la extracción termina (los dos pollers se apagan con la misma condición). El arreglo de
 * fondo es que `MemoryDocumentsView` acepte los documentos por prop, o que Fuentes deje de montarse
 * en este acto — que es lo que hace el mockup: ahí la biblioteca se abre desde «Ver y gestionar».
 */
export function FeedActContent({
    clientId,
    // Sube cuando algo entró a la memoria por FUERA de este componente (el drop sobre el orbe, que
    // vive en `BoosterFeedHero`). Es el aviso de que hay una fila nueva que traer.
    refreshToken = 0,
    // El documento que acaba de entrar (`{id, title}`), para narrarlo. Lo baja el contenedor porque
    // las dos puertas de carga —la Barra Total y el drop sobre el orbe— viven en `BoosterFeedHero`,
    // hermano de éste, y el narrador vive acá.
    lastIngested = null,
    // Se llama cuando un documento de ESTA visita termina de extraerse. Es lo que le permite al
    // resto de la página (el mazo de Revisa) enterarse de que hay hechos nuevos sin volver a
    // pollear el mismo endpoint por su cuenta.
    onDocumentReady,
    onProcessingStateChange,
    pollDocuments = true,
}) {
    const {t} = useTranslation();
    const [documents, setDocuments] = useState([]);
    const scopeRef = useRef(clientId);
    scopeRef.current = clientId;
    const [noteCount, setNoteCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [narration, setNarration] = useState({text: '', id: 0});
    // Se agotaron los reintentos: dejamos de saber en qué estado está lo que se está extrayendo. Es
    // un estado de PANTALLA, no un detalle interno — las filas dejan de anunciar una fase que ya no
    // estamos siguiendo y la lista ofrece volver a mirar.
    const [stalled, setStalled] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    // El último documento de ESTA visita que terminó de extraerse: `{id, title, facts}`. Es lo que
    // la línea de fuentes muestra unos segundos como confirmación ("se guardó · N hechos") antes de
    // volver al conteo. Sale de la misma transición a READY que dispara al narrador.
    const [fresh, setFresh] = useState(null);

    const processingStateRef = useRef(onProcessingStateChange);
    processingStateRef.current = onProcessingStateChange;
    useEffect(() => {
        processingStateRef.current?.({clientId, loading, stalled, unknown: loadFailed,
            active: documents.some(doc => isInFlight(doc) && !doc.stale)});
    }, [clientId, documents, loading, stalled, loadFailed]);

    const aliveRef = useRef(true);
    const pollRef = useRef(null);
    const fetchRef = useRef(null);
    const failuresRef = useRef(0);
    const requestSequenceRef = useRef(0);
    // Estados de la vuelta anterior: con ellos se detecta la TRANSICIÓN (EXTRACTING → READY), que es
    // lo que el narrador cuenta. Mirando sólo el estado actual, cada poll volvería a anunciar lo
    // mismo.
    const previousRef = useRef(new Map());
    // Por ref y no como dependencia de `announce`: el callback lo recrea el padre en cada render y
    // meterlo en las deps volvería a armar la cadena de fetch/poll a cada rato.
    const onDocumentReadyRef = useRef(onDocumentReady);
    onDocumentReadyRef.current = onDocumentReady;
    // Los ids que ya existían en la primera carga. Todo lo que aparece después entró en esta visita,
    // sin importar por qué puerta (la Barra Total, el drop sobre el orbe, o la carga de Fuentes de al lado).
    const baselineRef = useRef(null);

    const narrate = useCallback((text) => {
        setNarration(previous => ({text, id: previous.id + 1}));
    }, []);

    const clearPoll = useCallback(() => {
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const announce = useCallback((docs) => {
        const before = previousRef.current;
        const baseline = baselineRef.current;
        let becameReady = false;
        let lastReady = null;
        docs.forEach((doc) => {
            const changed = before.get(doc.id) !== doc.status;
            // La TRANSICIÓN a READY importa siempre, aunque el documento ya estuviera ahí al abrir
            // la pantalla: significa que hay hechos nuevos del otro lado. El caso concreto es
            // recargar mientras algo se está extrayendo — ese documento está en el baseline, así
            // que con el guard de abajo primero nunca avisaba, ni al mazo de Revisa ni al checklist.
            if (changed && doc.status === DOC_STATUS.READY) becameReady = true;
            // Lo que sí se limita a esta visita es lo que se DICE: al abrir la pantalla la
            // biblioteca vieja entera está READY y narrarla sería un aluvión de frases sobre
            // documentos de marzo.
            if (!baseline || baseline.has(doc.id)) return;
            if (!changed) return;
            const title = doc.title || '';
            if (doc.status === DOC_STATUS.READY) {
                // Un material que se procesa sin extraer hechos (un informe: sólo se guarda su
                // resumen) no tiene «0 hechos encontrados»: no se buscaron (QA-alim-N02).
                const result = doc.extraction_result;
                const facts = result?.disposition === 'SUMMARY_ONLY' ? null : result?.counts?.created;
                lastReady = {id: doc.id, title, facts: typeof facts === 'number' ? facts : null};
                narrate(typeof facts === 'number' && facts > 0
                    ? t('ia_booster.feed.narrator_read_facts',
                        {title, n: facts, interpolation: {escapeValue: false}})
                    : t('ia_booster.feed.narrator_read',
                        {title, interpolation: {escapeValue: false}}));
            } else if (doc.status === DOC_STATUS.FAILED) {
                narrate(t('ia_booster.feed.narrator_failed',
                    {title, interpolation: {escapeValue: false}}));
            }
        });
        // Terminó de extraer: los hechos nuevos ya existen del otro lado. Se avisa hacia AFUERA
        // porque el mazo de Revisa está en la misma página, treinta píxeles más abajo, y sin esto
        // seguía vacío hasta un F5 — justo la cadena que narra el umbral ("lo que Dashbo encuentra
        // baja aquí a revisión").
        //
        // El aviso sale de acá y no de un vigía aparte por una razón concreta: este componente ya
        // pollea y ya calcula la TRANSICIÓN (la necesita el narrador). Un observador externo que
        // quisiera lo mismo tendría que volver a pedir el listado por su cuenta, o sea un segundo
        // poll del mismo endpoint para un dato que acá ya está resuelto.
        //
        // Una vuelta puede traer varios documentos listos: es UN aviso, no uno por documento.
        if (lastReady) setFresh(lastReady);
        // El mismo aviso refresca el Paquete de Contexto de la guía de arriba (`FeedGuideBlock`): un
        // documento que llegó a READY ya tiene su `document_kind` y puede haber tachado un ítem.
        if (becameReady && onDocumentReadyRef.current) onDocumentReadyRef.current();
    }, [narrate, t]);

    const schedulePoll = useCallback(() => {
        clearPoll();
        pollRef.current = setTimeout(() => fetchRef.current(true), FEED_POLL_MS);
    }, [clearPoll]);

    /**
     * Una vuelta que no trajo el listado.
     *
     * La regla es que una respuesta mala NO puede matar el poll: se vuelve a agendar hasta
     * `MAX_POLL_RETRIES` veces seguidas, y recién ahí se admite en pantalla que dejamos de seguir
     * la extracción. Un `return` a secas dejaba las filas en «Clasificando…» para siempre.
     *
     * La carga VISIBLE (la del montaje y la del cambio de cliente) sigue como antes: ahí no hay
     * nada en vuelo que sostener y el toast ya cuenta lo que pasó.
     */
    const handleBadRound = useCallback((silent) => {
        setLoadFailed(true);
        if (!silent) {
            setStalled(true);
            toast.error(t('client_memory.toast.load_error'));
            return;
        }
        if (!pollDocuments) return;
        failuresRef.current += 1;
        if (failuresRef.current <= MAX_POLL_RETRIES) {
            schedulePoll();
            return;
        }
        clearPoll();
        setStalled(true);
    }, [clearPoll, pollDocuments, schedulePoll, t]);

    const fetchAll = useCallback((silent = false) => {
        if (!clientId) return;
        const sequence = ++requestSequenceRef.current;
        if (!silent) setLoading(true);
        Promise.all([
            ClientMemoryApi.listDocuments(clientId),
            ClientMemoryApi.listMemories(clientId),
        ]).then(([docsResponse, notesResponse]) => {
            if (!aliveRef.current || scopeRef.current !== clientId || sequence !== requestSequenceRef.current) return;
            setLoading(false);
            // Los listados devuelven el array directo ({documents}/{memories}), sin {status}.
            if (!docsResponse || !Array.isArray(docsResponse.documents)) {
                handleBadRound(silent);
                return;
            }
            // Llegó el listado: se perdona lo anterior. El tope cuenta fallas SEGUIDAS, no fallas
            // de toda la sesión — si no, una tarde larga con dos blips espaciados se rendiría.
            failuresRef.current = 0;
            setStalled(false);
            setLoadFailed(false);
            const next = docsResponse.documents;
            if (baselineRef.current === null) {
                baselineRef.current = new Set(next.map(doc => doc.id));
            } else {
                announce(next);
            }
            previousRef.current = new Map(next.map(doc => [doc.id, doc.status]));
            setDocuments(next);
            if (notesResponse && Array.isArray(notesResponse.memories)) {
                setNoteCount(notesResponse.memories.length);
            }
            clearPoll();
            if (pollDocuments && next.some(isInFlight)) schedulePoll();
        }).catch(() => {
            if (!aliveRef.current || scopeRef.current !== clientId || sequence !== requestSequenceRef.current) return;
            setLoading(false);
            handleBadRound(silent);
        });
    }, [announce, clearPoll, clientId, handleBadRound, pollDocuments, schedulePoll]);

    // El timer llama al fetch por ref: si lo capturara por closure, cada `fetchAll` nuevo dejaría
    // agendada la versión vieja y el poll se quedaría con un `clientId` (o un `pollDocuments`)
    // caduco.
    fetchRef.current = fetchAll;

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
            clearPoll();
        };
    }, [clearPoll]);

    useEffect(() => {
        // Cambiar de cliente reinicia el acto entero: la baseline del cliente anterior marcaría como
        // "recién entrados" a todos los documentos del nuevo, y el poll agendado traería una vuelta
        // más del cliente que ya no se está mirando.
        clearPoll();
        setLoading(true);
        setDocuments([]);
        baselineRef.current = null;
        previousRef.current = new Map();
        failuresRef.current = 0;
        setStalled(false);
        setFresh(null);
        fetchRef.current(false);
    }, [clearPoll, clientId]);

    // Material nuevo, por la puerta que sea, estrena presupuesto de reintentos: si el poll se había
    // rendido en una racha mala, cargar algo vuelve a poner a la pantalla a seguirlo.
    const trackAgain = useCallback(() => {
        failuresRef.current = 0;
        setStalled(false);
    }, []);

    // La carga, el reintento y el reprocesado ya devuelven el documento con su corrida aceptada: se
    // suma a la lista en el acto, así Revisa lo ve en vuelo mientras llega el listado.
    const acceptProcessingDocument = useCallback((doc) => {
        if (!doc?.id || !isInFlight(doc)) return;
        setDocuments(current => [doc, ...current.filter(item => item.id !== doc.id)]);
        trackAgain();
    }, [trackAgain]);

    // Ingesta por fuera de este componente (la Barra Total y el drop sobre el orbe). El primer valor
    // no dispara nada: sería un segundo fetch en el montaje.
    //
    // Acá también se narra («Leyendo “X”…»), porque el narrador vive en este componente y las dos
    // puertas de carga están en el hermano. `lastIngested` se lee por ref: es el DATO del token, no
    // una dependencia — meterlo en las deps volvería a narrar cuando el contenedor re-renderiza sin
    // que haya entrado nada.
    const lastIngestedRef = useRef(lastIngested);
    lastIngestedRef.current = lastIngested;
    const firstTokenRef = useRef(refreshToken);
    const lastSeededRef = useRef(null);
    useEffect(() => {
        if (refreshToken === firstTokenRef.current) return;
        firstTokenRef.current = refreshToken;
        const doc = lastIngestedRef.current;
        if (doc?.id && doc !== lastSeededRef.current && isInFlight(doc)) {
            acceptProcessingDocument(doc);
            lastSeededRef.current = doc;
        }
        if (doc && doc.title) {
            narrate(t('ia_booster.feed.narrator_reading', {
                title: doc.title,
                interpolation: {escapeValue: false},
            }));
        }
        trackAgain();
        fetchRef.current(true);
    }, [acceptProcessingDocument, narrate, refreshToken, t, trackAgain]);

    // Documento que el usuario quiere volver a procesar, esperando confirmación. La pregunta dice
    // las dos consecuencias reales (gasto de IA, hechos duplicados), no un «¿estás seguro?».
    const [reprocessTarget, setReprocessTarget] = useState(null);
    const [kindTarget, setKindTarget] = useState(null);

    const handleReprocess = useCallback(() => {
        const doc = reprocessTarget;
        if (!doc) return;
        setReprocessTarget(null);
        setBusyId(doc.id);
        ClientMemoryApi.reprocessDocument(doc.id, clientId, {
            expectedRunId: doc.processing_run_id, documentKind: doc.correctionKind,
        }).then((data) => {
            if (!aliveRef.current || scopeRef.current !== clientId) return;
            setBusyId(null);
            if (data && data.status === 'OK') {
                acceptProcessingDocument(data.data);
                toast.success(t('client_memory.documents.reprocess_ok'));
                fetchRef.current(true);
            } else {
                toast.error(t('client_memory.toast.error'));
                fetchRef.current(true);
            }
        }).catch(() => {
            if (!aliveRef.current || scopeRef.current !== clientId) return;
            setBusyId(null);
            toast.error(t('client_memory.toast.error'));
        });
    }, [acceptProcessingDocument, clientId, reprocessTarget, t]);

    const handleRetry = useCallback((doc) => {
        setBusyId(doc.id);
        ClientMemoryApi.retryDocument(doc.id, clientId).then((data) => {
            if (!aliveRef.current || scopeRef.current !== clientId) return;
            setBusyId(null);
            if (data && data.status === 'OK') {
                acceptProcessingDocument(data.data);
                if (data.queued) {
                    // El backend le preguntó a la cola y la tarea sigue ahí: no se reintentó nada.
                    toast.info(t('client_memory.documents.retry_still_queued'));
                } else {
                    toast.success(t('client_memory.documents.retry_ok'));
                    narrate(t('ia_booster.feed.narrator_retry', {
                        title: doc.title || '',
                        interpolation: {escapeValue: false},
                    }));
                }
                fetchRef.current(true);
            } else {
                toast.error(t('client_memory.toast.error'));
            }
        }).catch(() => {
            if (!aliveRef.current || scopeRef.current !== clientId) return;
            setBusyId(null);
            toast.error(t('client_memory.toast.error'));
        });
    }, [acceptProcessingDocument, clientId, narrate, t]);

    // «Volver a mirar» del aviso de que perdimos el rastro. Va con `silent=false` a propósito: el
    // usuario lo pidió, así que si vuelve a fallar tiene que enterarse por un toast y no en silencio.
    const handleRefresh = useCallback(() => {
        trackAgain();
        fetchRef.current(false);
    }, [trackAgain]);

    // Lo que está pasando ahora (en vuelo) más lo que entró en esta visita. El resto de la
    // biblioteca no va acá: para eso está la línea de fuentes, que lleva a la tabla completa.
    const feedDocuments = useMemo(() => {
        const baseline = baselineRef.current;
        return documents
            .filter(doc => isInFlight(doc) || (baseline && !baseline.has(doc.id)))
            .slice(0, MAX_FEED_ITEMS);
    }, [documents]);

    return (
        <div className="iab-feed">
            {/* El rótulo del acto y la guía de qué material sirve ya no van acá: subieron arriba del
                orbe (`FeedGuideBlock`, card wugYmCtZ). */}
            <BoosterNarrator message={narration.text} messageId={narration.id}/>

            <IngestActivityList
                documents={feedDocuments}
                clientId={clientId}
                onRetry={handleRetry}
                onReprocess={setReprocessTarget}
                onCorrectKind={setKindTarget}
                busyId={busyId}
                stalled={stalled}
                onRefresh={handleRefresh}
            />

            <DocumentKindModal isOpen={!!kindTarget} doc={kindTarget}
                               toggle={() => setKindTarget(null)}
                               onConfirm={(kind) => {
                                   setReprocessTarget({...kindTarget, correctionKind: kind});
                                   setKindTarget(null);
                               }}/>

            {/* Misma pregunta que en la vista clásica: las dos consecuencias y lo que NO se pierde.
                El texto del audio es otro porque ahí son DOS llamadas al modelo, no una. */}
            <YesNoModal
                skipRenderButton
                className="cmem-confirm-costly"
                isOpen={!!reprocessTarget}
                title={t('client_memory.documents.reprocess_title')}
                question={reprocessTarget
                    ? t(reprocessTarget.is_audio
                        ? 'client_memory.documents.reprocess_question_audio'
                        : 'client_memory.documents.reprocess_question',
                    {title: reprocessTarget.title || t('client_memory.documents.untitled')})
                    : ''}
                isSaving={!!reprocessTarget && busyId === reprocessTarget.id}
                onYesAnswer={handleReprocess}
                onNoAnswer={() => setReprocessTarget(null)}
            />

            <SourceLine
                clientId={clientId}
                documentCount={loading ? null : documents.length}
                noteCount={noteCount}
                loading={loading}
                fresh={fresh}
            />
        </div>
    );
}

export default FeedActContent;
